/**
 * Verjaardag-keuze — DTO's en Zod-schemas.
 *
 * Spec §14 — types voor portal (eigen keuze) + backoffice (overzicht per jaar)
 * + planning-overlay (taart-icoontje op planbord).
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'

export const VERJAARDAG_KEUZE_TYPE = ['VRIJ', 'BETAALD_ALS_FEESTDAG'] as const
export type VerjaardagKeuzeType = (typeof VERJAARDAG_KEUZE_TYPE)[number]

export const VERJAARDAG_KEUZE_LABELS: Record<VerjaardagKeuzeType, string> = {
  VRIJ:                 'Vrij op mijn verjaardag',
  BETAALD_ALS_FEESTDAG: 'Werken tegen feestdagtarief (200%)',
}

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface VerjaardagKeuzeDTO {
  id: string
  employeeId: string
  employeeNaam: string
  employeeAfdeling: string | null
  jaar: number
  keuze: VerjaardagKeuzeType
  verjaardagDatum: string         // ISO-date (YYYY-MM-DD)
  dagVanDeWeek: string            // "maandag", "dinsdag", ...
  leeftijd: number
  isWerkdag: boolean
  gekozenOp: string | null        // ISO-datetime; null = nog niet gekozen
  isAutomatisch: boolean          // true als default op 31-jan deadline
  planningId: string | null
  verwerktInPayroll: boolean
  opmerking: string | null
}

export interface VerjaardagKeuzeOverzichtDTO {
  jaar: number
  totaalMedewerkers: number       // alle actieve medewerkers met birthDate
  totaalOpWerkdag: number         // met isWerkdag=true (bevraagbaar)
  aantalVrij: number
  aantalBetaald: number
  aantalNietGekozen: number       // isWerkdag=true && gekozenOp=null
  keuzes: VerjaardagKeuzeDTO[]
}

/**
 * Planning-overlay DTO (spec §7.1).
 *
 * Batch-48: het planbord leest de verjaardag NIET meer hier maar uit de
 * planbord-DTO zelf (`PlanningBoardEmployee.verjaardagen`) - die hangt aan
 * `PLANNING.canView`, terwijl dit endpoint `MEDEWERKERS.canView` eist, waardoor
 * een planner zonder medewerkersrechten het icoontje nooit zag. Dit endpoint
 * blijft bestaan voor overige overlay-consumenten.
 *
 * `leeftijd` is hier bewust VERWIJDERD (batch-48 criterium 4): een planning-
 * overlay heeft aan dag + keuze genoeg; leeftijd/geboortejaar blijven
 * afgeschermd. Wie de leeftijd nodig heeft, leest de HR-overzichten
 * (`VerjaardagKeuzeDTO`) met de bijbehorende VERLOF-permissie.
 */
export interface PlanningVerjaardagDTO {
  employeeId: string
  employeeNaam: string
  datum: string                   // ISO-date
  keuze: VerjaardagKeuzeType | null // null als nog niet gekozen (januari)
}

/**
 * Self-service samenvatting voor portal — alleen huidig jaar.
 *
 * `wijzigbaarTot` is de cut-off (14 dagen voor verjaardag). Daarna is de
 * keuze definitief.
 */
export interface VerjaardagKeuzeHuidigDTO {
  id: string | null               // null = nog geen record (zou alleen voor (recent in dienst getreden) employees moeten kunnen)
  jaar: number
  verjaardagDatum: string | null  // null als birthDate niet ingevuld
  dagVanDeWeek: string | null
  leeftijd: number | null
  isWerkdag: boolean
  keuze: VerjaardagKeuzeType | null
  gekozenOp: string | null
  wijzigbaarTot: string | null    // null als niet-werkdag (niets te wijzigen)
  wijzigbaar: boolean
}

// ─── Zod request schemas ─────────────────────────────────────────────────────

export const createVerjaardagKeuzeSchema = z.object({
  keuze:     z.enum(VERJAARDAG_KEUZE_TYPE),
  opmerking: z.string().trim().max(500).optional(),
})

export type CreateVerjaardagKeuzeInput = z.infer<typeof createVerjaardagKeuzeSchema>

export const updateVerjaardagKeuzeSchema = z.object({
  keuze:     z.enum(VERJAARDAG_KEUZE_TYPE),
  opmerking: z.string().trim().max(500).optional(),
})

export type UpdateVerjaardagKeuzeInput = z.infer<typeof updateVerjaardagKeuzeSchema>

/**
 * Backoffice admin-override — HR/admin kan eens vastgelegde keuze aanpassen.
 */
export const adminUpdateVerjaardagKeuzeSchema = z.object({
  keuze:     z.enum(VERJAARDAG_KEUZE_TYPE).optional(),
  opmerking: z.string().trim().max(500).nullable().optional(),
})

export type AdminUpdateVerjaardagKeuzeInput = z.infer<typeof adminUpdateVerjaardagKeuzeSchema>

export const listVerjaardagKeuzesQuerySchema = z.object({
  jaar:        z.coerce.number().int().min(2020).max(2100),
  afdeling:    z.string().trim().max(100).optional(),
  keuzeType:   z.enum(VERJAARDAG_KEUZE_TYPE).optional(),
  nietGekozen: queryBoolean().optional(),
})

export type ListVerjaardagKeuzesQuery = z.infer<typeof listVerjaardagKeuzesQuerySchema>

export const planningVerjaardagenQuerySchema = z.object({
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'start moet YYYY-MM-DD zijn'),
  end:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'end moet YYYY-MM-DD zijn'),
})

export type PlanningVerjaardagenQuery = z.infer<typeof planningVerjaardagenQuerySchema>

// Re-export idParam zodat callers `import { idParam } from '@streep/types'` blijven gebruiken.
export { idParam }
